import { syncLiveRegionVisibility } from '../../core/helpers';

const getPrefixFromSearchState = () => {
  const from_search = document.body.classList.contains('has-overlay-search');
  return from_search ? 'overlay-search-' : '';
};

const getPrefixFromOpenedOverlay = () => {
  const overlaySearchOpened = document.querySelector('[data-overlay-search]')?.classList.contains('opened');
  return overlaySearchOpened ? 'overlay-search-' : '';
};

const getPrefixFromTrigger = (target) => {
  if (!(target instanceof HTMLElement)) {
    return '';
  }

  if (
    target.matches('[data-overlay-search-show-mobile-sort-nav]')
    || target.closest('[data-overlay-search-show-mobile-sort-nav]')
    || target.closest('[data-overlay-search]')
  ) {
    return 'overlay-search-';
  }

  return '';
};

const MOBILE_FILTER_PREFIXES = ['', 'overlay-search-'];
const mobileFilterTopResizeObservers = new Map();
const mobileFilterTopMutationObservers = new Map();
let mobileFilterTopResizeListenerInitialized = false;
const mobileFilterLastFocusedElementByPrefix = new Map();

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

const getDrawerId = (prefix = '') => (prefix ? `mobile-${prefix}filter-nav-drawer` : 'mobile-filter-nav-drawer');
const getDrawerOpenClass = (prefix = '') => `mobile-${prefix}filter-nav-opened`;
const getMobileFilterNav = (prefix = '') => document.querySelector(`[data-mobile-${prefix}filter-nav]`);

const getMobileSortingOption = (prefix = '') => {
  const mobileFilterNav = getMobileFilterNav(prefix);
  const mobileFilterNavOptions = mobileFilterNav?.querySelector(`[data-mobile-${prefix}filter-nav-options]`);

  return mobileFilterNavOptions?.querySelector(
    `[data-${prefix}filter-options-type="sorting"], [data-filter-options-type="sorting"]`
  ) || null;
};

const getFilterOptionTypeFromTarget = (target, prefix = '') => {
  if (!(target instanceof HTMLElement)) {
    return null;
  }

  const explicitType = target.getAttribute(`data-${prefix}filter-options-type`);
  if (explicitType) {
    return explicitType;
  }

  const filterTrigger = target.closest(`[data-${prefix}show-individual-filter]`);
  const triggerType = filterTrigger?.getAttribute(`data-${prefix}filter-options-type`);
  if (triggerType) {
    return triggerType;
  }

  const option = target.closest('.filter-option');
  return option?.getAttribute(`data-${prefix}filter-options-type`) || null;
};

const scrollMobileFilterNavToTop = (prefix = '') => {
  const mobileFilterNav = getMobileFilterNav(prefix);
  const scrollContainer = mobileFilterNav?.querySelector(`.mobile-${prefix}filter-nav__fixed`)
    || mobileFilterNav?.querySelector(`[data-mobile-${prefix}filter-nav-options]`)
    || mobileFilterNav;

  if (scrollContainer instanceof HTMLElement) {
    scrollContainer.scrollTop = 0;
    scrollContainer.scrollTo({ top: 0, behavior: 'auto' });
  }
};

const scrollMobileFilterNavToElement = (prefix = '', element, { smooth = true } = {}) => {
  if (!(element instanceof HTMLElement)) {
    return false;
  }

  const mobileFilterNav = getMobileFilterNav(prefix);
  const scrollContainer = mobileFilterNav?.querySelector(`.mobile-${prefix}filter-nav__fixed`)
    || mobileFilterNav?.querySelector(`[data-mobile-${prefix}filter-nav-options]`)
    || mobileFilterNav;

  if (!(scrollContainer instanceof HTMLElement)) {
    return false;
  }

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const scrollBehavior = smooth && !isIOS ? 'smooth' : 'auto';
  const containerRect = scrollContainer.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  const elementTop = scrollContainer.scrollTop + (elementRect.top - containerRect.top);

  scrollContainer.scrollTo({
    top: Math.max(0, Math.round(elementTop - 8)),
    behavior: scrollBehavior,
  });

  return true;
};

const isElementVisible = (element) => {
  if (!(element instanceof HTMLElement)) {
    return false;
  }

  const style = window.getComputedStyle(element);
  return style.display !== 'none' && style.visibility !== 'hidden';
};

const getFocusableElements = (container) => {
  if (!container) {
    return [];
  }

  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(isElementVisible);
};

const getOpenedMobileFilterPrefix = () => {
  if (document.documentElement.classList.contains(getDrawerOpenClass('overlay-search-'))) {
    return 'overlay-search-';
  }

  if (document.documentElement.classList.contains(getDrawerOpenClass(''))) {
    return '';
  }

  return null;
};

const syncMobileFilterOptionA11y = (prefix = '') => {
  document.querySelectorAll(`[data-mobile-${prefix}filter-nav-options] [data-${prefix}filter-options]`).forEach((option) => {
    const isOpen = option.classList.contains('active');
    const button = option.querySelector(`[data-${prefix}filter-button]`);
    const panel = option.querySelector(`[data-${prefix}filter-options-container]`);

    if (button) {
      button.setAttribute('aria-expanded', String(isOpen));
    }

    if (panel) {
      panel.setAttribute('aria-hidden', String(!isOpen));

      // Keep focusable controls out of the tab order while the panel is
      // aria-hidden — otherwise keyboard users can Tab into inputs assistive
      // tech can't see, which is also what makes some a11y scanners report
      // the group itself as unlabeled.
      panel.querySelectorAll('input, button, a[href], select, textarea').forEach((control) => {
        control.setAttribute('tabindex', isOpen ? '0' : '-1');
      });
    }
  });

  syncLiveRegionVisibility(getMobileFilterNav(prefix));
};

const setMobileFilterTriggerState = (prefix = '', isOpen = false) => {
  const isOverlay = prefix === 'overlay-search-';
  const controls = getDrawerId(prefix);

  document.querySelectorAll('[data-show-mobile-filter-nav]').forEach((trigger) => {
    const inOverlay = Boolean(trigger.closest('[data-overlay-search]'));
    if (inOverlay !== isOverlay) {
      return;
    }

    trigger.setAttribute('role', trigger.tagName.toLowerCase() === 'button' ? 'button' : (trigger.getAttribute('role') || 'button'));
    trigger.setAttribute('aria-haspopup', 'dialog');
    trigger.setAttribute('aria-controls', controls);
    trigger.setAttribute('aria-expanded', String(isOpen));

    if (trigger.tagName.toLowerCase() !== 'button' && !trigger.hasAttribute('tabindex')) {
      trigger.setAttribute('tabindex', '0');
    }
  });

  const sortTriggerSelector = isOverlay ? '[data-overlay-search-show-mobile-sort-nav]' : '[data-show-mobile-sort-nav]';
  document.querySelectorAll(sortTriggerSelector).forEach((trigger) => {
    trigger.setAttribute('role', trigger.tagName.toLowerCase() === 'button' ? 'button' : (trigger.getAttribute('role') || 'button'));
    trigger.setAttribute('aria-haspopup', 'dialog');
    trigger.setAttribute('aria-controls', controls);
    trigger.setAttribute('aria-expanded', String(isOpen));

    if (trigger.tagName.toLowerCase() !== 'button' && !trigger.hasAttribute('tabindex')) {
      trigger.setAttribute('tabindex', '0');
    }
  });
};

/**
 * Sync the top height of the mobile filter nav and update the CSS variable accordingly
 * 
 * @param {string} prefix 
 * @returns {void}
 */
const syncMobileFilterTopHeight = (prefix = '') => {
  const nav = document.querySelector(`[data-mobile-${prefix}filter-nav]`);
  if (!nav) {
    return;
  }

  const top = nav.querySelector(`.mobile-${prefix}filter-nav__top`);
  if (!top) {
    return;
  }

  const topHeight = Math.ceil(top.getBoundingClientRect().height);
  nav.style.setProperty('--mobile-filter-top-height', `${topHeight}px`);
};

/**
 * Watch the top height of the mobile filter nav and update the CSS variable accordingly
 * 
 * @param {string} prefix 
 * @returns {void}
 */
const watchMobileFilterTopHeight = (prefix = '') => {
  const nav = document.querySelector(`[data-mobile-${prefix}filter-nav]`);
  if (!nav || mobileFilterTopResizeObservers.has(prefix)) {
    return;
  }

  const top = nav.querySelector(`.mobile-${prefix}filter-nav__top`);
  if (!top) {
    return;
  }

  syncMobileFilterTopHeight(prefix);

  if ('ResizeObserver' in window) {
    const observer = new ResizeObserver(() => {
      syncMobileFilterTopHeight(prefix);
    });

    observer.observe(top);
    mobileFilterTopResizeObservers.set(prefix, observer);
  } else if ('MutationObserver' in window && !mobileFilterTopMutationObservers.has(prefix)) {
    const mutationObserver = new MutationObserver(() => {
      syncMobileFilterTopHeight(prefix);
    });

    mutationObserver.observe(top, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    });

    mobileFilterTopMutationObservers.set(prefix, mutationObserver);
  }

  if (!mobileFilterTopResizeListenerInitialized) {
    mobileFilterTopResizeListenerInitialized = true;

    window.addEventListener('resize', () => {
      MOBILE_FILTER_PREFIXES.forEach((currentPrefix) => {
        syncMobileFilterTopHeight(currentPrefix);
      });
    });
  }
};

/**
 * Sync the top heights of all mobile filter navs - Needed since we can dynamically add selected filters on top of the drawer
 * 
 * @returns {void}
 */
const syncAllMobileFilterTopHeights = () => {
  MOBILE_FILTER_PREFIXES.forEach((prefix) => {
    watchMobileFilterTopHeight(prefix);
    syncMobileFilterTopHeight(prefix);
  });
};

/**
 * Open the mobile filter drawer
 * 
 * @param {HTMLElement} target - The target element that triggered the opening of the mobile filter nav
 * @returns {void}
 */
const openMobileFilterNav = (target, options = {}) => {
  const { openSorting = false, scrollToSorting = false, prefix: providedPrefix = null } = options;
  const prefix = providedPrefix ?? getPrefixFromSearchState();
  const requestedFilterOptionType = openSorting ? null : getFilterOptionTypeFromTarget(target, prefix);
  MOBILE_FILTER_PREFIXES.forEach((currentPrefix) => {
    document.documentElement.classList.remove(getDrawerOpenClass(currentPrefix));
  });
  document.documentElement.classList.add(getDrawerOpenClass(prefix));

  const mobileFilterNav = getMobileFilterNav(prefix);
  if (mobileFilterNav) {
    mobileFilterNav.setAttribute('aria-hidden', 'false');
  }

  const activeElement = document.activeElement;
  if (activeElement instanceof HTMLElement) {
    mobileFilterLastFocusedElementByPrefix.set(prefix, activeElement);
  }

  setMobileFilterTriggerState(prefix, true);
  syncMobileFilterTopHeight(prefix);

  requestAnimationFrame(() => {
    syncMobileFilterTopHeight(prefix);

    const focusableElements = getFocusableElements(mobileFilterNav);
    const firstFocusable = focusableElements[0] || mobileFilterNav;

    if (firstFocusable instanceof HTMLElement) {
      firstFocusable.focus();
    }
  });

  // Open only sorting or the relevant filter option
  document.querySelectorAll(`[data-mobile-${prefix}filter-nav-options] .filter-option`).forEach((element) => {
    const filterType = element.getAttribute(`data-${prefix}filter-options-type`);

    if (openSorting === true && filterType === 'sorting') {
      element.classList.add('active');
      return;
    }

    if (requestedFilterOptionType && filterType === requestedFilterOptionType) {
      element.classList.add('active');
      return;
    }

    element.classList.remove('active');
  });

  syncMobileFilterOptionA11y(prefix);

  // Resolve a single scroll destination to avoid scroll race conditions.
  const executeDrawerScroll = () => {
    if (scrollToSorting === true) {
      const sortingOption = getMobileSortingOption(prefix);
      if (scrollMobileFilterNavToElement(prefix, sortingOption, { smooth: true })) {
        return;
      }
    }

    if (requestedFilterOptionType) {
      const mobileFilterNav = getMobileFilterNav(prefix);
      const mobileFilterNavOptions = mobileFilterNav?.querySelector(`[data-mobile-${prefix}filter-nav-options]`);
      const targetFilterOption = mobileFilterNavOptions?.querySelector(
        `[data-${prefix}filter-options-type="${requestedFilterOptionType}"], [data-filter-options-type="${requestedFilterOptionType}"]`
      );

      if (scrollMobileFilterNavToElement(prefix, targetFilterOption, { smooth: true })) {
        return;
      }
    }

    scrollMobileFilterNavToTop(prefix);
  };

  // Run after the drawer layout settles; iOS needs an extra frame for reliable scroll positioning.
  window.setTimeout(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        executeDrawerScroll();
      });
    });
  }, 220);
};

/**
 * Close the mobile filter nav
 * 
 * @returns {void}
 */
const closeMobileFilterNav = () => {
  const prefix = getOpenedMobileFilterPrefix() ?? getPrefixFromSearchState();
  document.documentElement.classList.remove(getDrawerOpenClass(prefix));

  // Always reset drawer scroll when closed so next open starts at the top.
  scrollMobileFilterNavToTop(prefix);

  const mobileFilterNav = getMobileFilterNav(prefix);
  if (mobileFilterNav) {
    mobileFilterNav.setAttribute('aria-hidden', 'true');
  }

  setMobileFilterTriggerState(prefix, false);
  syncMobileFilterOptionA11y(prefix);

  const lastFocusedElement = mobileFilterLastFocusedElementByPrefix.get(prefix);
  if (lastFocusedElement instanceof HTMLElement) {
    lastFocusedElement.focus();
  }

  // Close all filters in nav
  document.querySelectorAll(`[data-mobile-${prefix}filter-nav-options] .filter-option`).forEach((element) => {
    element.classList.remove('active');
  });
};

/**
 * Bind an event listener to a selector using event delegation
 * 
 * @param {string} eventName 
 * @param {string} selector 
 * @param {Function} handler
 */
const bindDelegated = (eventName, selector, handler) => {
  document.addEventListener(eventName, (e) => {
    const target = e.target.closest(selector);
    if (!target) {
      return;
    }

    handler(e, target);
  });
};

const TAP_GHOST_CLICK_DELAY = 500;
let lastTouchTimestamp = 0;

/**
 * Bind a tap event to a selector, handling both touchend and click events
 * 
 * @param {string} selector 
 * @param {Function} handler
 */
const bindTap = (selector, handler) => {
  bindDelegated('touchend', selector, (e, target) => {
    lastTouchTimestamp = Date.now();
    handler(e, target);
  });

  bindDelegated('click', selector, (e, target) => {
    if (Date.now() - lastTouchTimestamp < TAP_GHOST_CLICK_DELAY) {
      return;
    }

    handler(e, target);
  });
};

const bindKeyboardActivation = (selector) => {
  bindDelegated('keydown', selector, (e, target) => {
    if (target.tagName.toLowerCase() === 'button') {
      return;
    }

    if (e.key !== 'Enter' && e.key !== ' ') {
      return;
    }

    e.preventDefault();
    target.click();
  });
};

let mobileFilterNavEventsInitialized = false;
const mobileFiltersInitializedByPrefix = new Set();
let mobileFiltersGlobalEventsInitialized = false;

/**
 * Initialize the mobile filter nav events
 * 
 * @returns {void}
 */
const initMobileFilterNav = () => {
  if (mobileFilterNavEventsInitialized) {
    return;
  }

  mobileFilterNavEventsInitialized = true;
  syncAllMobileFilterTopHeights();

  bindKeyboardActivation('[data-show-mobile-filter-nav], [data-show-mobile-sort-nav], [data-overlay-search-show-mobile-sort-nav]');
  bindDelegated('keydown', '[data-mobile-filter-nav], [data-mobile-overlay-search-filter-nav]', (e) => {
    const prefix = getOpenedMobileFilterPrefix();
    if (prefix === null) {
      return;
    }

    const mobileFilterNav = getMobileFilterNav(prefix);
    if (!mobileFilterNav) {
      return;
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      closeMobileFilterNav();
      return;
    }

    if (e.key !== 'Tab') {
      return;
    }

    const focusableElements = getFocusableElements(mobileFilterNav);
    if (!focusableElements.length) {
      e.preventDefault();
      mobileFilterNav.focus();
      return;
    }

    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];
    const activeElement = document.activeElement;

    if (e.shiftKey && activeElement === firstFocusable) {
      e.preventDefault();
      lastFocusable.focus();
      return;
    }

    if (!e.shiftKey && activeElement === lastFocusable) {
      e.preventDefault();
      firstFocusable.focus();
    }
  });

  MOBILE_FILTER_PREFIXES.forEach((prefix) => {
    setMobileFilterTriggerState(prefix, false);
    syncMobileFilterOptionA11y(prefix);
  });

  bindTap('[data-show-mobile-filter-nav]', (e, target) => {
    e.stopPropagation();
    e.preventDefault();
    const prefix = getPrefixFromOpenedOverlay();
    const isOpen = document.documentElement.classList.contains(getDrawerOpenClass(prefix));

    if (isOpen) {
      closeMobileFilterNav();
      return;
    }

    openMobileFilterNav(target, { prefix });
  });

  bindTap('[data-show-mobile-sort-nav], [data-overlay-search-show-mobile-sort-nav]', (e, target) => {
    e.stopPropagation();
    e.preventDefault();
    const prefix = getPrefixFromTrigger(target);
    openMobileFilterNav(target, { openSorting: true, scrollToSorting: true, prefix });
  });

  bindTap('[data-mobile-filter-nav-close]', (e) => {
    e.stopPropagation();
    e.preventDefault();
    closeMobileFilterNav();
  });

  bindTap('[data-sorting-close-mobile]', (_e, target) => {
    const sortingToggle = target.closest('.filter-button-mobile-container')?.querySelector('[data-show-mobile-sorting-nav]');
    sortingToggle?.click();
  });

  bindTap('[data-show-mobile-sorting-nav]', (e) => {
    e.stopPropagation();
    e.preventDefault();

    const prefix = getPrefixFromSearchState();
    document.querySelectorAll(`[data-mobile-${prefix}filter-sorting]`).forEach((element) => {
      element.classList.toggle('active');
    });

    document.querySelectorAll('[data-sorting-close-mobile]').forEach((element) => {
      element.classList.toggle('active');
    });
  });
};

/**
 * Initialize the mobile (drawer) filters
 * 
 * @param {boolean} from_search 
 * @returns {void}
 */
const initMobileFilters = (from_search) => {
  initMobileFilterNav();

  const prefix = (from_search) ? 'overlay-search-' : '';
  watchMobileFilterTopHeight(prefix);
  syncMobileFilterTopHeight(prefix);

  if (!mobileFiltersInitializedByPrefix.has(prefix)) {
    mobileFiltersInitializedByPrefix.add(prefix);

    const clearActiveOptions = (single_option_only = null) => {
      if (single_option_only) {
        single_option_only.classList.remove('active');
        return;
      }

      document.querySelectorAll(`[data-mobile-${prefix}filter-nav-options] [data-${prefix}filter-options]`).forEach((element) => {
        element.classList.remove('active');
      });

      document.querySelectorAll(`[data-mobile-${prefix}filter-sorting]`).forEach((element) => {
        element.classList.remove('active');
      });

      syncMobileFilterOptionA11y(prefix);
    };

    bindTap(`[data-mobile-${prefix}filter-nav-options] [data-${prefix}filter-button]`, (_e, target) => {
      const option = target.closest('li');
      if (!option) {
        return;
      }

      const previous_state_active = option.classList.contains('active');
      clearActiveOptions(option);

      if (!previous_state_active) {
        option.classList.add('active');
      }

      syncMobileFilterOptionA11y(prefix);
    });

    bindTap(`[data-mobile-${prefix}filter-sorting] [data-${prefix}filter-options-title]`, (_e, target) => {
      const option = target.parentElement;
      if (!option) {
        return;
      }

      const previous_state_active = option.classList.contains('active');
      clearActiveOptions(option);

      if (!previous_state_active) {
        option.classList.add('active');
      }

      syncMobileFilterOptionA11y(prefix);
    });
  }

  if (!mobileFiltersGlobalEventsInitialized) {
    mobileFiltersGlobalEventsInitialized = true;

    bindTap('[data-mobile-filter-nav-done]', (e) => {
      e.stopPropagation();
      e.preventDefault();
      closeMobileFilterNav();
    });

    // Close mobile filter nav when breakpoint changes to desktop
    window.addEventListener('breakpointHasChanged', (e) => {
      const current_breakpoint = e.detail;
      const isMobileFilterOpen = document.documentElement.classList.contains('mobile-filter-nav-opened')
        || document.documentElement.classList.contains('mobile-overlay-search-filter-nav-opened');

      if (current_breakpoint !== 'mobile' && isMobileFilterOpen) {
        document.querySelectorAll('[data-close-drawer]').forEach((element) => {
          element.click();
        });
      }
    });
  }
};

export {
  initMobileFilters,
  initMobileFilterNav,
  openMobileFilterNav,
  closeMobileFilterNav,
};
